/** 金额与日期展示 —— 与后端 platform/money.ts、platform/time.ts 口径一致。 */

/** 140000 → "1,400.00" */
export function amount(sen: number | null | undefined): string {
  if (sen === null || sen === undefined) return '—'
  const neg = sen < 0
  const abs = Math.abs(Math.round(sen))
  const major = Math.floor(abs / 100)
  const minor = abs % 100
  return `${neg ? '-' : ''}${major.toLocaleString('en-MY')}.${String(minor).padStart(2, '0')}`
}

/** 140000 → "RM 1,400.00"。null 用于「无权限查看」，显示 — 而不是 RM 0.00 */
export function rm(sen: number | null | undefined): string {
  if (sen === null || sen === undefined) return '—'
  return `RM ${amount(sen)}`
}

/** "1,400.50" / "1400.5" → 140050 sen。解析失败返回 null。 */
export function parseAmount(input: string): number | null {
  const raw = input.replace(/[\s,]/g, '').replace(/^RM/i, '')
  if (!/^\d+(\.\d{1,2})?$/.test(raw)) return null
  const [i = '0', d = ''] = raw.split('.')
  return Number(i) * 100 + Number((d + '00').slice(0, 2))
}

const TZ_OFFSET_MS = 8 * 60 * 60 * 1000

function localParts(d: Date) {
  const s = new Date(d.getTime() + TZ_OFFSET_MS)
  return {
    y: s.getUTCFullYear(),
    m: s.getUTCMonth() + 1,
    d: s.getUTCDate(),
    h: s.getUTCHours(),
    min: s.getUTCMinutes(),
    dow: s.getUTCDay(),
  }
}

/** 马来西亚惯例 DD/MM/YYYY */
export function date(v: string | Date | null | undefined): string {
  if (!v) return '—'
  const d = typeof v === 'string' ? new Date(v) : v
  if (Number.isNaN(d.getTime())) return '—'
  const p = localParts(d)
  return `${String(p.d).padStart(2, '0')}/${String(p.m).padStart(2, '0')}/${p.y}`
}

/** 不带年份，用于同年内的紧凑显示 */
export function dateShort(v: string | Date | null | undefined): string {
  if (!v) return '—'
  const d = typeof v === 'string' ? new Date(v) : v
  const p = localParts(d)
  return `${String(p.d).padStart(2, '0')}/${String(p.m).padStart(2, '0')}`
}

export function time(v: string | Date | null | undefined): string {
  if (!v) return '—'
  const d = typeof v === 'string' ? new Date(v) : v
  const p = localParts(d)
  return `${String(p.h).padStart(2, '0')}:${String(p.min).padStart(2, '0')}`
}

export const WEEKDAYS = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六']
export const WEEKDAYS_SHORT = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']

export function weekday(v: string | Date): string {
  const d = typeof v === 'string' ? new Date(v) : v
  return WEEKDAYS[localParts(d).dow] ?? ''
}

/** 本地日期键 YYYY-MM-DD，用于与后端对齐 */
export function dateKey(v: string | Date): string {
  const d = typeof v === 'string' ? new Date(v) : v
  const p = localParts(d)
  return `${p.y}-${String(p.m).padStart(2, '0')}-${String(p.d).padStart(2, '0')}`
}

export function parseDateKey(key: string): Date {
  const [y, m, d] = key.split('-').map(Number)
  return new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1) - TZ_OFFSET_MS)
}

export function addDays(v: Date, n: number): Date {
  return new Date(v.getTime() + n * 86400000)
}

/** 「4 天前」「今天」「明天」—— 老板真正在意的是相对时间，不是绝对日期 */
export function relativeDay(v: string | Date | null | undefined): string {
  if (!v) return '—'
  const d = typeof v === 'string' ? new Date(v) : v
  const diff = Math.round(
    (parseDateKey(dateKey(d)).getTime() - parseDateKey(dateKey(new Date())).getTime()) / 86400000,
  )
  if (diff === 0) return '今天'
  if (diff === 1) return '明天'
  if (diff === -1) return '昨天'
  if (diff < 0) return `${-diff} 天前`
  return `${diff} 天后`
}

export const CASE_TYPE_ZH: Record<string, string> = {
  FULL_DENTURE: '全口义齿',
  PARTIAL_DENTURE: '局部义齿',
  REPAIR: '假牙维修',
  ADD_TOOTH: '加牙',
  RELINE: '重衬',
  ADJUSTMENT: '调整',
}

export const CASE_TYPE_SHORT: Record<string, string> = {
  FULL_DENTURE: '全口',
  PARTIAL_DENTURE: '局部',
  REPAIR: '维修',
  ADD_TOOTH: '加牙',
  RELINE: '重衬',
  ADJUSTMENT: '调整',
}

export const ARCH_ZH: Record<string, string> = {
  UPPER: '上颌',
  LOWER: '下颌',
  BOTH: '双颌',
}

export const CASE_STATUS_ZH: Record<string, string> = {
  DRAFT: '草稿',
  IN_PROGRESS: '进行中',
  READY: '可取件',
  DELIVERED: '已交付',
  CLOSED: '已完成',
  ON_HOLD: '暂停',
  CANCELLED: '已取消',
}

export const APPT_STATUS_ZH: Record<string, string> = {
  BOOKED: '已约',
  CONFIRMED: '已确认',
  ARRIVED: '已到店',
  DONE: '已完成',
  NO_SHOW: '爽约',
  CANCELLED: '已取消',
}

export const PAYMENT_KIND_ZH: Record<string, string> = {
  DEPOSIT: '定金',
  INSTALLMENT: '分期',
  FINAL: '尾款',
  REFUND: '退款',
}

export const PAYMENT_METHOD_ZH: Record<string, string> = {
  CASH: '现金',
  DUITNOW: 'DuitNow',
  TRANSFER: '转账',
  OTHER: '其他',
}

export const LANG_ZH: Record<string, string> = {
  ms: 'Bahasa Melayu',
  en: 'English',
  zh: '中文',
}

export const REMINDER_TYPE_ZH: Record<string, string> = {
  APPT_T24: '预约提醒（提前一天）',
  APPT_T2: '预约提醒（当天）',
  READY_PICKUP: '可取件通知',
  BALANCE_DUE: '尾款提醒',
  PICKUP_NUDGE: '催取件',
  CHECKUP_6M: '半年保养提醒',
  RENEWAL_5Y: '换新提醒',
  REFERRAL_REWARD: '转介绍奖励',
}
